import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Linkedin } from "lucide-react";
import { OPERATORS } from "@/data/operators";

const EASE = [0.16, 1, 0.3, 1];

function initials(name) {
  return name
    .split(" ")
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function OperatorCard({ op, index }) {
  const hasDetail = Boolean(op.detail);
  const hasLinkedin = Boolean(op.linkedin);
  const clickable = hasDetail || hasLinkedin;

  const cardClass = `group block overflow-hidden rounded-[6px] border border-white/10 bg-[#111111] transition-colors duration-300 ${
    clickable ? "hover:border-gold/50" : ""
  }`;

  const Inner = (
    <>
      {/* portrait / monogram */}
      <div className="relative overflow-hidden" style={{ aspectRatio: "4 / 5" }}>
        {op.image ? (
          <img
            src={op.image}
            alt={`${op.name}, ${op.role} of ${op.company}`}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover object-top grayscale transition-all duration-700 group-hover:grayscale-0 group-hover:scale-[1.03]"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-[linear-gradient(155deg,#171717_0%,#0d0d0d_100%)]">
            <span className="font-serif text-[4.5rem] font-bold text-white/12 transition-colors duration-500 group-hover:text-gold/30">
              {initials(op.name)}
            </span>
          </div>
        )}
        <div className="pointer-events-none absolute inset-0 [background:linear-gradient(180deg,transparent_55%,rgba(10,10,10,0.85)_100%)]" />
        <span className="absolute bottom-4 left-4 text-[0.6rem] font-semibold uppercase tracking-[0.24em] text-gold">
          {op.company}
        </span>
      </div>
      {/* meta */}
      <div className="flex items-start justify-between gap-4 p-6">
        <div>
          <h3 className="font-serif text-2xl font-bold leading-none text-cream">
            {op.name}
          </h3>
          <p className="mt-2 text-[0.7rem] font-medium uppercase tracking-[0.2em] text-mute">
            {op.role} &middot; {op.company}
          </p>
        </div>
        {hasDetail ? (
          <ArrowUpRight className="mt-1 h-5 w-5 shrink-0 text-mute transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-gold" />
        ) : hasLinkedin ? (
          <Linkedin className="mt-1 h-5 w-5 shrink-0 text-mute transition-all duration-300 group-hover:-translate-y-0.5 group-hover:text-gold" />
        ) : null}
      </div>
    </>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-8%" }}
      transition={{ duration: 0.7, ease: EASE, delay: index * 0.08 }}
    >
      {hasDetail ? (
        <Link data-testid={`operator-card-${op.slug}`} to={`/operators/${op.slug}`} className={cardClass}>
          {Inner}
        </Link>
      ) : hasLinkedin ? (
        <a
          data-testid={`operator-card-${op.slug}`}
          href={op.linkedin}
          target="_blank"
          rel="noopener noreferrer"
          className={cardClass}
        >
          {Inner}
        </a>
      ) : (
        <div data-testid={`operator-card-${op.slug}`} className={cardClass}>
          {Inner}
        </div>
      )}
    </motion.div>
  );
}

export default function Operators() {
  useEffect(() => {
    const prev = document.title;
    document.title = "Operators — Govil Ventures";
    return () => {
      document.title = prev;
    };
  }, []);

  return (
    <div data-testid="operators-page" className="min-h-screen bg-noir text-cream">
      {/* header */}
      <section className="border-b border-white/10 px-6 pt-36 pb-16 md:px-12 md:pt-44 md:pb-20">
        <div className="mx-auto max-w-7xl">
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="mb-6 text-[0.72rem] font-semibold uppercase tracking-[0.34em] text-gold"
          >
            Meet the Operators
          </motion.p>
          <motion.h1
            data-testid="operators-heading"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
            className="max-w-[18ch] font-serif font-bold leading-[0.98] tracking-tight text-cream"
            style={{ fontSize: "clamp(2.4rem, 5.4vw, 4.5rem)" }}
          >
            The people building inside the model.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.2 }}
            className="mt-7 max-w-xl text-[1.05rem] leading-relaxed text-mute"
          >
            Experienced operators who brought a career of judgment &mdash; and let us
            build the rest of the company around them.
          </motion.p>
        </div>
      </section>

      {/* grid */}
      <section className="px-6 py-16 md:px-12 md:py-24">
        <div
          data-testid="operators-grid"
          className="mx-auto grid max-w-7xl grid-cols-1 gap-7 sm:grid-cols-2 lg:grid-cols-3"
        >
          {OPERATORS.map((op, i) => (
            <OperatorCard key={op.slug} op={op} index={i} />
          ))}
        </div>
      </section>
    </div>
  );
}
